export interface StoredScheduledFact {
  id?: number;
  scheduled_date: string;
  type?: string;
  title: string;
}

const STORAGE_PREFIX = "scheduled_fact_";

type Maybe<T> = T | undefined;

function isLocalStorageAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function buildKey(id: number | string): string {
  return `${STORAGE_PREFIX}${id}`;
}

function parseStoredFact(raw: string | null): Maybe<StoredScheduledFact> {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as StoredScheduledFact;
    if (parsed && typeof parsed === "object" && typeof parsed.title === "string") {
      return parsed;
    }
  } catch (error) {
    // Ignore malformed entries
  }
  return undefined;
}

export function saveScheduledFact(id: number | string, fact: StoredScheduledFact): void {
  if (!isLocalStorageAvailable()) return;
  try {
    window.localStorage.setItem(buildKey(id), JSON.stringify(fact));
  } catch (error) {
    // Swallow quota or access errors silently
  }
}

export function saveScheduledFactWithKey(key: string, fact: StoredScheduledFact): void {
  if (!isLocalStorageAvailable()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(fact));
  } catch (error) {
    // Ignore
  }
}

export function removeScheduledFact(id: number | string): void {
  if (!isLocalStorageAvailable()) return;
  try {
    window.localStorage.removeItem(buildKey(id));
  } catch (error) {
    // Ignore
  }
}

export function removeScheduledFactByKey(key: string): void {
  if (!isLocalStorageAvailable()) return;
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    // Ignore
  }
}

export function getStoredScheduledFact(id: number | string): Maybe<StoredScheduledFact> {
  if (!isLocalStorageAvailable()) return undefined;
  return parseStoredFact(window.localStorage.getItem(buildKey(id)));
}

export function getStoredScheduledFactByKey(key: string): Maybe<StoredScheduledFact> {
  if (!isLocalStorageAvailable()) return undefined;
  return parseStoredFact(window.localStorage.getItem(key));
}

export function findScheduledFactByTitleAndType(title: string, type?: string): Maybe<StoredScheduledFact> {
  if (!isLocalStorageAvailable()) return undefined;
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (!key || !key.startsWith(STORAGE_PREFIX)) continue;
    const stored = parseStoredFact(window.localStorage.getItem(key));
    if (!stored) continue;
    const matchesTitle = stored.title === title;
    const matchesType = !type || !stored.type || stored.type === type;
    if (matchesTitle && matchesType) {
      return stored;
    }
  }
  return undefined;
}

export function buildScheduledFactKey(id: number | string): string {
  return buildKey(id);
}
